import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder from '@tiptap/extension-placeholder';
import Text from '@tiptap/extension-text';
import Typography from '@tiptap/extension-typography';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import debounce from 'lodash.debounce';
import React, { useEffect } from 'react';
import Underlines from '../underlines/Underlines';
import './styles.css';
import useStore from 'store';
import { useHasActiveBlockError } from 'store/selectors/message';
import useSaveElement from 'v1/hooks/use-save-element';
import { Box } from 'v1/ui';

type Props = {
  content: string;
  placeholder: string;
  srId: string;
  updateContent: (content: string) => void;
};

export default function PlainText({
  content,
  placeholder,
  srId,
  updateContent,
}: Props) {
  const handleFieldFocused = useStore.useHandleFieldFocused();

  // Check if there is a block error for this srId
  const hasBlockError = useHasActiveBlockError(srId);

  const editor = useEditor({
    extensions: [
      Document,
      History,
      Paragraph,
      Placeholder.configure({ placeholder }),
      Text,
      Typography,
    ],
    content: textToTipTapHtml(content),
    onFocus: () => handleFieldFocused(srId),
    editorProps: {
      attributes: {
        id: srId,
      },
    },
  });

  const [containerRef] = useSaveElement(srId);

  const debouncedSendUpdate = debounce((editor: Editor) => {
    updateContent(editor.getText({ blockSeparator: '\n' }));
  }, 100);

  // Listen for updates from Firebase
  useEffect(() => {
    if (editor) {
      if (content !== editor.getText({ blockSeparator: '\n' })) {
        editor.commands.setContent(textToTipTapHtml(content));
      }
    }
  }, [content, editor]);

  // Listen for changes from RichText editor
  useEffect(() => {
    if (editor) {
      const update = () => {
        debouncedSendUpdate(editor);
      };
      editor.on('update', update);

      return () => {
        editor.off('update', update);
      };
    }
  }, [editor]); // eslint-disable-line

  if (!editor) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        mb: 1,
        '.ProseMirror': {
          borderColor: hasBlockError ? 'rgba(142, 0, 254, 1)' : undefined,
        },
      }}
    >
      <Underlines srId={srId} />
      <EditorContent editor={editor} className="editor-container" />
    </Box>
  );
}

// Turn text with newlines in paragraphs so TipTap preserves line breaks
function textToTipTapHtml(text: string): string {
  let html: string = '';
  const blocks = text.split('\n');
  blocks.forEach((block) => (html = `${html}<p>${block}</p>`));
  return html;
}
